/**
 * Entity 9 — Facelings.
 *
 * Lore: humanoids that share almost everything with people — height, build,
 * clothing, the way they walk — except that where the face should be there is
 * nothing at all. The wiki's own line is the design brief: they "resemble
 * normal humans enough from afar", and it is closing the distance that ruins
 * it. So this is deliberately the most ordinary figure in the game. Shirt,
 * trousers, shoes, a head. Nothing about the silhouette is monstrous.
 *
 * The behaviour freezes it whenever it is being watched, so stillness is the
 * whole effect and this file's only real job is to be *completely* still: the
 * walk cycle is driven by distance travelled rather than by the clock, so the
 * frame it stops on is the frame it holds — one foot off the floor, head
 * turned a few degrees off its shoulders, for as long as you keep looking. It
 * does not breathe and it does not sway. Nothing here reads `time`.
 */

import * as THREE from 'three';
import { clamp, clamp01, damp, TAU, angleDelta, renderYaw } from '../core/math';
import type { EntityView } from './api';
import { bone, castShadows, chain, dampAngle, freeAll, Kit, planarSpeed, type Disposable } from './entkit';

/** Metres per second below which it counts as stopped, and everything holds. */
const STILL = 0.06;
/** Radians of gait per metre travelled. One full cycle every metre and a half. */
const CADENCE = TAU / 1.5;

/** Set so the shoes touch the floor at the extremes of the stride, not only mid-step. */
const HIP_Y = 1.25;
const THIGH = 0.6;
const SHIN = 0.58;
const UPPER_ARM = 0.34;
const FOREARM = 0.32;

/**
 * Facelings vary the way a crowd varies. Three shirts is enough for that to
 * register without any of them becoming the memorable one.
 */
const SHIRTS: readonly number[] = [0x59677a, 0x6d6455, 0x4d5a4c];

interface FacelingRig {
  root: THREE.Group;
  torso: THREE.Group;
  head: THREE.Group;
  legs: THREE.Group[][];
  arms: THREE.Group[][];
}

interface FacelingKit extends Disposable {
  make(shirt: number): FacelingRig;
}

function buildFacelingKit(): FacelingKit {
  // Matte and slightly grey. Under a torch it looks like a shop mannequin,
  // which is what every account says: too even, and not quite the colour of a
  // person.
  const skin = new THREE.MeshLambertMaterial({ color: 0xb4aa9d });
  const trouser = new THREE.MeshLambertMaterial({ color: 0x34353b });
  const shoe = new THREE.MeshLambertMaterial({ color: 0x1b1b1e });
  const shirts = SHIRTS.map((c) => new THREE.MeshLambertMaterial({ color: c }));

  const torsoGeo = new THREE.CapsuleGeometry(0.135, 0.44, 4, 9);
  torsoGeo.scale(1, 1, 0.78);
  torsoGeo.translate(0, 0.35, 0);
  const shoulderGeo = new THREE.SphereGeometry(0.12, 8, 5);
  shoulderGeo.scale(1.62, 0.66, 0.9);
  shoulderGeo.translate(0, 0.6, 0);
  const hipGeo = new THREE.CapsuleGeometry(0.13, 0.12, 3, 8);
  hipGeo.scale(1, 1, 0.8);
  hipGeo.translate(0, 0.05, 0);
  const neckGeo = new THREE.CylinderGeometry(0.043, 0.052, 0.12, 6);
  neckGeo.translate(0, 0.65, 0);

  // Smooth on purpose, and finely enough divided that no facet gives the eye
  // anywhere to land. A blank face only works if it is genuinely blank.
  const headGeo = new THREE.SphereGeometry(0.108, 12, 8);
  headGeo.scale(0.9, 1.22, 1.0);

  const legGeos = [bone(THIGH, 0.082, 0.062, 6), bone(SHIN, 0.06, 0.045, 6)];
  const shoeGeo = new THREE.BoxGeometry(0.082, 0.05, 0.19);
  shoeGeo.translate(0, -SHIN - 0.02, -0.038);

  const armGeos = [bone(UPPER_ARM, 0.052, 0.042, 6), bone(FOREARM, 0.041, 0.032, 6)];
  const handGeo = new THREE.CapsuleGeometry(0.03, 0.06, 2, 5);
  handGeo.scale(0.72, 1, 1.25);
  handGeo.translate(0, -FOREARM - 0.055, 0);

  const geos = [torsoGeo, shoulderGeo, hipGeo, neckGeo, headGeo, ...legGeos, shoeGeo, ...armGeos, handGeo];

  return {
    make(shirt: number): FacelingRig {
      const cloth = shirts[shirt % shirts.length]!;
      const root = new THREE.Group();

      const hips = new THREE.Mesh(hipGeo, trouser);
      hips.position.y = HIP_Y;
      root.add(hips);

      const torso = new THREE.Group();
      torso.position.y = HIP_Y;
      root.add(torso);
      torso.add(
        new THREE.Mesh(torsoGeo, cloth),
        new THREE.Mesh(shoulderGeo, cloth),
        new THREE.Mesh(neckGeo, skin)
      );

      const head = new THREE.Group();
      // A centimetre or two further forward than it should be. Not enough to
      // name, enough to make the neck look wrong when it is standing still.
      head.position.set(0, 0.79, -0.018);
      head.add(new THREE.Mesh(headGeo, skin));
      torso.add(head);

      const legs: THREE.Group[][] = [];
      for (const side of [-1, 1] as const) {
        const joints = chain(legGeos, [THIGH, SHIN], trouser);
        joints[0]!.position.set(side * 0.088, HIP_Y, 0);
        joints[1]!.add(new THREE.Mesh(shoeGeo, shoe));
        root.add(joints[0]!);
        legs.push(joints);
      }

      const arms: THREE.Group[][] = [];
      for (const side of [-1, 1] as const) {
        const joints = chain(armGeos, [UPPER_ARM, FOREARM], skin);
        joints[0]!.position.set(side * 0.185, 0.58, 0);
        // Held a few degrees off the body, the way a figure in a shop window
        // is posed rather than the way a person stands.
        joints[0]!.rotation.z = side * 0.115;
        joints[1]!.add(new THREE.Mesh(handGeo, skin));
        torso.add(joints[0]!);
        arms.push(joints);
      }

      castShadows(root);
      return { root, torso, head, legs, arms };
    },
    dispose(): void {
      freeAll(geos);
      freeAll([skin, trouser, shoe, ...shirts]);
    },
  };
}

const facelingKit = new Kit(buildFacelingKit);

/**
 * Build a Faceling. It walks like a person until the moment you look at it,
 * and then it is a thing that has stopped.
 */
export function createFacelingView(): EntityView {
  const kit = facelingKit.acquire();
  // Cosmetic per-instance variety: a shirt and a height, both fixed for life.
  const rig = kit.make((Math.random() * SHIRTS.length) | 0);
  const object = new THREE.Group();
  object.add(rig.root);
  rig.root.scale.setScalar(0.95 + Math.random() * 0.09);

  let stride = Math.random() * TAU;
  let yaw = 0;
  let yawInit = false;
  let headYaw = 0;
  let dead = 0;

  return {
    object,
    update(state, dt, _time, camera) {
      if (!yawInit) {
        yaw = state.yaw;
        yawInit = true;
      }
      object.position.set(state.pos.x, 0, state.pos.z);

      const speed = planarSpeed(state);
      // Everything that could still be moving after it stops is gated on this.
      // A body that settles into its pose over the next half second has not
      // frozen; it has slowed down, and the difference is the entity.
      if (speed > STILL) {
        const step = speed * dt;
        stride += step * CADENCE;
        yaw = dampAngle(yaw, state.yaw, 6, dt);

        // It looks where it is going, or at the player once it has them. Both
        // stop the instant it does, so a Faceling caught mid-turn stays
        // mid-turn with its head off its shoulders.
        const toPlayer = Math.atan2(camera.position.x - state.pos.x, -(camera.position.z - state.pos.z));
        const want = state.awareness > 0.3 ? clamp(angleDelta(yaw, toPlayer), -1.15, 1.15) : 0;
        headYaw = dampAngle(headYaw, want, 3.2, dt);
      }
      object.rotation.y = renderYaw(yaw);

      dead = damp(dead, state.mode === 'dead' ? 1 : 0, 2.4, dt);
      const alive = 1 - dead;

      // An ordinary walk on a body that is too tall for it. The gait itself is
      // not the horror and must not try to be.
      const swing = 0.1 + clamp01(speed / 2.4) * 0.46;
      for (let i = 0; i < 2; i++) {
        const p = stride + i * Math.PI;
        const leg = rig.legs[i]!;
        leg[0]!.rotation.x = Math.sin(p) * swing;
        leg[1]!.rotation.x = -Math.max(0, Math.sin(p + 0.75)) * swing * 1.05 - 0.04;

        const arm = rig.arms[i]!;
        arm[0]!.rotation.x = Math.sin(p + Math.PI) * swing * 0.62;
        arm[1]!.rotation.x = -0.12 - Math.max(0, Math.sin(p + Math.PI + 0.5)) * swing * 0.5;
      }

      // Vertical bob comes off the stride, so it too holds when the stride
      // does. No breath, no idle sway, nothing on a clock.
      rig.root.position.y = (Math.abs(Math.sin(stride)) - 0.5) * 0.026 * alive - dead * 0.18;
      rig.root.rotation.z = dead * 1.4;
      rig.torso.rotation.x = 0.02;
      rig.head.rotation.y = headYaw * alive;
    },
    dispose() {
      object.clear();
      facelingKit.release();
    },
  };
}
